import React from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import { toast } from "react-toastify";

const modalStyle = {
  position: "absolute",
  top: "5vh",
  left: "50%",
  transform: "translateX(-50%)",
  width: "min(1040px, calc(100vw - 32px))",
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "14px",
  boxShadow: 24,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const fieldSx = {
  "& .MuiInputLabel-root": {
    color: "#64748b",
    fontSize: "0.875rem",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#6d28d9",
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    fontSize: "0.875rem",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#cbd5e1",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#94a3b8",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6d28d9",
      borderWidth: "1px",
    },
  },
};

const checkboxSx = {
  color: "#94a3b8",
  padding: "4px",
  "&.Mui-checked": {
    color: "#6d28d9",
  },
};

const sourceDateOffsetOptions = [
  { value: 0, label: "Same day" },
  { value: 1, label: "Following day" },
  { value: -1, label: "Previous day" },
];

function getDefaultSourceMetricName(company) {
  return String(company?.revenue_metric_name || "").trim() || "Revenue";
}

function normalizeSourceDateOffset(value) {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) ? parsedValue : 0;
}

function getSourceDateOffsetLabel(value) {
  const normalizedValue = normalizeSourceDateOffset(value);
  const option = sourceDateOffsetOptions.find(
    (sourceDateOption) => sourceDateOption.value === normalizedValue
  );
  if (option) {
    return option.label;
  }
  if (normalizedValue > 0) {
    return `${normalizedValue} days after`;
  }
  return `${Math.abs(normalizedValue)} days before`;
}

function buildEmptyForm(sourceMetricName = "Revenue") {
  return {
    labor_driver_routed_sales_uuid: "",
    labor_driver_uuid: "",
    name: "",
    target_store_id: "",
    source_metric_name: sourceMetricName,
    source_store_ids: [],
    include_target_store_sales: true,
    source_date_offset_days: 0,
    is_active: true,
  };
}

function getRoutedConfig(driver) {
  return driver?.labor_driver_routed_sales || null;
}

function getStoreName(stores, storeId) {
  const match = stores.find((store) => String(store.id) === String(storeId));
  return match?.store_name || "Unknown";
}

function getCompanyBaseMetricNames(company) {
  return [
    "Revenue",
    "Orders",
    "Units",
    company?.revenue_metric_name,
    company?.orders_metric_name,
    company?.units_metric_name,
  ]
    .map((name) => String(name || "").trim())
    .filter(Boolean);
}

function getSourceMetricOptions(laborDrivers, company) {
  const names = new Set(getCompanyBaseMetricNames(company));
  (laborDrivers || []).forEach((driver) => {
    if (driver?.labor_driver_sales) {
      names.add(driver.name);
    }
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

function buildFormFromDriver(driver, defaultSourceMetricName = "Revenue") {
  const routedConfig = getRoutedConfig(driver);
  if (!routedConfig) {
    return buildEmptyForm(defaultSourceMetricName);
  }

  return {
    labor_driver_routed_sales_uuid: routedConfig.uuid || "",
    labor_driver_uuid: driver.uuid || "",
    name: driver.name || "",
    target_store_id: routedConfig.target_store || "",
    source_metric_name:
      routedConfig.source_metric_name || defaultSourceMetricName,
    source_store_ids: (routedConfig.source_stores || []).map((store) => store.id),
    include_target_store_sales: routedConfig.include_target_store_sales !== false,
    source_date_offset_days: normalizeSourceDateOffset(
      routedConfig.source_date_offset_days
    ),
    is_active: routedConfig.is_active !== false,
  };
}

function LaborDriverRoutedSalesModal({
  open,
  onClose,
  laborDrivers,
  storesInfo,
  onSave,
  onBackfill,
  onDelete,
  selectedCompany,
}) {
  const stores = Array.isArray(storesInfo) ? storesInfo : [];
  const defaultSourceMetricName = getDefaultSourceMetricName(selectedCompany);
  const routedDrivers = React.useMemo(
    () =>
      (laborDrivers || []).filter((driver) => driver.labor_driver_routed_sales),
    [laborDrivers]
  );
  const sourceMetricOptions = React.useMemo(
    () => getSourceMetricOptions(laborDrivers, selectedCompany),
    [laborDrivers, selectedCompany]
  );

  const [form, setForm] = React.useState(() =>
    buildEmptyForm(defaultSourceMetricName)
  );
  const [saving, setSaving] = React.useState(false);
  const [backfilling, setBackfilling] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [backfillResults, setBackfillResults] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setForm(buildEmptyForm(defaultSourceMetricName));
      setBackfillResults(null);
    }
  }, [open, defaultSourceMetricName]);

  const sourceStoreOptions = stores.filter(
    (store) => String(store.id) !== String(form.target_store_id)
  );
  const visibleSourceMetricOptions = sourceMetricOptions.includes(
    form.source_metric_name
  )
    ? sourceMetricOptions
    : [...sourceMetricOptions, form.source_metric_name].filter(Boolean).sort();

  function updateForm(key, value) {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function handleTargetChange(event) {
    const targetStoreId = event.target.value;
    setForm((current) => ({
      ...current,
      target_store_id: targetStoreId,
      source_store_ids: current.source_store_ids.filter(
        (storeId) => String(storeId) !== String(targetStoreId)
      ),
    }));
  }

  function handleEdit(driver) {
    setForm(buildFormFromDriver(driver, defaultSourceMetricName));
    setBackfillResults(null);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error("Enter an output sales group name");
      return;
    }
    if (!form.target_store_id) {
      toast.error("Select a target location");
      return;
    }
    if (!form.source_metric_name) {
      toast.error("Select a source sales group");
      return;
    }
    if (
      form.source_store_ids.length === 0 &&
      !form.include_target_store_sales
    ) {
      toast.error("Select a source location or include the target location");
      return;
    }
    if (Math.abs(normalizeSourceDateOffset(form.source_date_offset_days)) > 31) {
      toast.error("Source date offset must be between -31 and 31 days");
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...form,
        name: form.name.trim(),
        source_date_offset_days: normalizeSourceDateOffset(
          form.source_date_offset_days
        ),
      });
      setForm(buildEmptyForm(defaultSourceMetricName));
    } finally {
      setSaving(false);
    }
  }

  async function handleBackfill(driverUuid) {
    if (!onBackfill) {
      return;
    }
    setBackfilling(true);
    try {
      const payload = await onBackfill(driverUuid ? [driverUuid] : undefined);
      setBackfillResults(payload);
      toast.success("Routed sales backfill complete", { autoClose: 3000 });
    } catch (error) {
      toast.error(error.message || "Backfill request failed", { autoClose: 4000 });
    } finally {
      setBackfilling(false);
    }
  }

  async function handleDelete() {
    if (!form.labor_driver_uuid || !onDelete) {
      return;
    }
    setDeleting(true);
    try {
      await onDelete(form.labor_driver_uuid);
      setForm(buildEmptyForm(defaultSourceMetricName));
    } finally {
      setDeleting(false);
    }
  }

  function formatSourceStores(driver) {
    const routedConfig = getRoutedConfig(driver);
    const sourceStores = routedConfig?.source_stores || [];
    const sourceStoreNames = sourceStores.map((store) => store.store_name);
    if (routedConfig?.include_target_store_sales) {
      sourceStoreNames.unshift(routedConfig.target_store_name);
    }
    return sourceStoreNames.join(", ") || "None";
  }

  function formatBackfillResults() {
    const rows = backfillResults?.rules || [];
    if (!rows.length) {
      return null;
    }
    const totalRows = rows.reduce(
      (sum, row) =>
        sum +
        Number(row.actual_daily_rows || 0) +
        Number(row.actual_intraday_rows || 0) +
        Number(row.forecasted_daily_rows || 0) +
        Number(row.forecasted_intraday_rows || 0),
      0
    );
    return `${rows.length} route runs, ${totalRows.toLocaleString()} rows written`;
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Advanced Routing
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Route one location's sales basis from selected source locations.
            </p>
          </div>
          <button
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            onClick={onClose}
            type="button"
          >
            <CloseIcon style={{ fontSize: "20px" }} />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 overflow-y-auto px-5 py-4 md:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">
                Routed Sales Groups
              </h3>
              {!!routedDrivers.length && (
                <button
                  className="rounded-md px-2 py-1 text-xs font-semibold text-violet-700 transition-colors hover:bg-violet-50 disabled:cursor-not-allowed disabled:text-slate-400"
                  disabled={backfilling}
                  onClick={() => handleBackfill()}
                  type="button"
                >
                  Backfill all
                </button>
              )}
            </div>

            <div className="mt-3 overflow-hidden rounded-lg border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="py-2.5 pl-3 pr-3 text-left text-xs font-semibold text-slate-500">
                      Output
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">
                      Target
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">
                      Sources
                    </th>
                    <th className="py-2.5 pl-3 pr-3 text-right text-xs font-semibold text-slate-500">
                      Status
                    </th>
                    <th className="py-2.5 pl-3 pr-3 text-right text-xs font-semibold text-slate-500">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {routedDrivers.map((driver) => {
                    const routedConfig = getRoutedConfig(driver);
                    return (
                      <tr
                        key={driver.uuid}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleEdit(driver)}
                      >
                        <td className="py-3 pl-3 pr-3 text-sm text-slate-900">
                          <p className="font-medium">{driver.name}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            From {routedConfig.source_metric_name} -{" "}
                            {getSourceDateOffsetLabel(
                              routedConfig.source_date_offset_days
                            )}
                          </p>
                        </td>
                        <td className="px-3 py-3 text-sm text-slate-900">
                          {routedConfig.target_store_name}
                        </td>
                        <td className="px-3 py-3 text-sm text-slate-600">
                          {formatSourceStores(driver)}
                        </td>
                        <td className="py-3 pl-3 pr-3 text-right text-sm">
                          <span
                            className={`rounded-md px-2 py-1 text-xs font-medium ${
                              routedConfig.is_active
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {routedConfig.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="py-3 pl-3 pr-3 text-right text-sm">
                          {onBackfill ? (
                            <LoadingButton
                              size="small"
                              variant="outlined"
                              loading={backfilling}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleBackfill(driver.uuid);
                              }}
                              sx={{
                                textTransform: "none",
                                borderColor: "#cbd5e1",
                                borderRadius: "8px",
                                color: "#475569",
                                minWidth: "82px",
                                "&:hover": {
                                  borderColor: "#a78bfa",
                                  backgroundColor: "#f5f3ff",
                                  color: "#6d28d9",
                                },
                              }}
                            >
                              Backfill
                            </LoadingButton>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                  {!routedDrivers.length && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-3 py-12 text-center text-sm text-slate-500"
                      >
                        No routed sales groups configured.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {formatBackfillResults() && (
              <p className="mt-3 text-sm text-slate-500">
                {formatBackfillResults()}
              </p>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">
                {form.labor_driver_routed_sales_uuid ? "Edit Route" : "New Route"}
              </h3>
              {form.labor_driver_routed_sales_uuid && (
                <button
                  className="rounded-md px-2 py-1 text-xs font-semibold text-slate-500 transition-colors hover:bg-white hover:text-slate-800"
                  onClick={() =>
                    setForm(buildEmptyForm(defaultSourceMetricName))
                  }
                  type="button"
                >
                  Clear
                </button>
              )}
            </div>

            <div className="mt-4 space-y-3">
              <TextField
                fullWidth
                label="Output sales group"
                size="small"
                sx={fieldSx}
                value={form.name}
                onChange={(event) => updateForm("name", event.target.value)}
              />

              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Target location</InputLabel>
                <Select
                  label="Target location"
                  value={form.target_store_id}
                  onChange={handleTargetChange}
                >
                  {stores.map((store) => (
                    <MenuItem key={store.id} value={store.id}>
                      {store.store_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Source sales group</InputLabel>
                <Select
                  label="Source sales group"
                  value={form.source_metric_name}
                  onChange={(event) =>
                    updateForm("source_metric_name", event.target.value)
                  }
                >
                  {visibleSourceMetricOptions.map((metricName) => (
                    <MenuItem key={metricName} value={metricName}>
                      {metricName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Source date</InputLabel>
                <Select
                  label="Source date"
                  value={form.source_date_offset_days}
                  onChange={(event) =>
                    updateForm(
                      "source_date_offset_days",
                      normalizeSourceDateOffset(event.target.value)
                    )
                  }
                >
                  {sourceDateOffsetOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Source locations</InputLabel>
                <Select
                  multiple
                  label="Source locations"
                  value={form.source_store_ids}
                  onChange={(event) =>
                    updateForm(
                      "source_store_ids",
                      (
                        Array.isArray(event.target.value)
                          ? event.target.value
                          : event.target.value.split(",")
                      ).map((storeId) => Number(storeId))
                    )
                  }
                  renderValue={(selected) =>
                    selected
                      .map((storeId) => getStoreName(stores, storeId))
                      .join(", ")
                  }
                >
                  {sourceStoreOptions.map((store) => (
                    <MenuItem key={store.id} value={store.id}>
                      <Checkbox
                        checked={form.source_store_ids.includes(store.id)}
                        sx={checkboxSx}
                      />
                      <ListItemText primary={store.store_name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="mt-4 space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox
                  checked={form.include_target_store_sales}
                  onChange={(event) =>
                    updateForm("include_target_store_sales", event.target.checked)
                  }
                  sx={checkboxSx}
                />
                Include target location sales
              </label>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox
                  checked={form.is_active}
                  onChange={(event) =>
                    updateForm("is_active", event.target.checked)
                  }
                  sx={checkboxSx}
                />
                Active
              </label>
            </div>

            <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-200 pt-4">
              {form.labor_driver_routed_sales_uuid && (
                <LoadingButton
                  variant="text"
                  color="error"
                  loading={deleting}
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleDelete}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    paddingX: "10px",
                  }}
                >
                  Delete
                </LoadingButton>
              )}

              <div className="ml-auto flex items-center gap-2">
                <LoadingButton
                  variant="contained"
                  loading={saving}
                  loadingPosition="end"
                  endIcon={<SaveIcon />}
                  onClick={handleSave}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    backgroundColor: "#6d28d9",
                    boxShadow: "none",
                    paddingX: "18px",
                    "&:hover": {
                      backgroundColor: "#5b21b6",
                      boxShadow: "none",
                    },
                  }}
                >
                  Save
                </LoadingButton>
              </div>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default LaborDriverRoutedSalesModal;
